import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../../showcase/shared/ShowcasePageWrapper';
import { SLDS_CHART_COLORS } from '../../showcase/shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
  stripe: '#635BFF',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '16px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: COLORS.textPrimary,
  fontWeight: '400',
  marginBottom: '16px',
};

const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block', backgroundColor: bgColor, color: color,
    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', lineHeight: '20px',
  }}>
    {label}
  </span>
);

const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? COLORS.brand : COLORS.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

const QuickActionButton = ({ icon, label }) => (
  <button style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '12px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.border}`,
    borderRadius: '4px', cursor: 'pointer', width: '100%',
  }}>
    <Icon category="utility" name={icon} size="small" style={{ fill: COLORS.brand }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>{label}</span>
  </button>
);

// ---------------------------------------------------------------------------
// Stage Path Component
// ---------------------------------------------------------------------------
const StagePath = ({ stages, currentStage }) => {
  const currentIndex = stages.indexOf(currentStage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginTop: '12px' }}>
      {stages.map((stage, idx) => {
        const isComplete = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        const bgColor = isComplete ? COLORS.success : isCurrent ? COLORS.brand : '#dddbda';
        const textColor = isComplete || isCurrent ? 'white' : '#706e6b';
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              flex: 1, backgroundColor: bgColor, color: textColor,
              padding: '8px 16px', fontSize: '12px', fontWeight: '600', textAlign: 'center',
              borderRadius: idx === 0 ? '4px 0 0 4px' : idx === stages.length - 1 ? '0 4px 4px 0' : '0',
              borderRight: idx < stages.length - 1 ? '2px solid white' : 'none',
              position: 'relative',
            }}>
              {isComplete && <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white', marginRight: '4px' }} />}
              {stage}
              {isCurrent && (
                <div style={{
                  position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
                  width: '0', height: '0', borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent', borderTop: `6px solid ${bgColor}`,
                }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '4px',
      marginBottom: '16px', display: 'flex', alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button key={tab} onClick={() => onTabChange(tab)} style={{
          padding: '12px 24px', fontSize: '14px',
          fontWeight: activeTab === tab ? '700' : '400',
          color: activeTab === tab ? COLORS.brand : '#3e3e3c',
          backgroundColor: 'transparent', border: 'none',
          borderBottom: activeTab === tab ? `3px solid ${COLORS.brand}` : '3px solid transparent',
          cursor: 'pointer',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Custom DataTable Cells
// ---------------------------------------------------------------------------
const PaymentStatusCell = ({ children, ...props }) => {
  const status = props.item && props.item.status;
  const statusColors = {
    'Succeeded': { bg: '#e8f5e9', color: '#2e844a', border: '#a8d8b0' },
    'Scheduled': { bg: '#eef4ff', color: '#0176d3', border: '#b9d6f7' },
  };
  const colorSet = statusColors[status] || { bg: '#f3f3f3', color: '#706e6b', border: '#dddbda' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color, border: `1px solid ${colorSet.border}`,
      }}>
        {status}
      </span>
    </DataTableCell>
  );
};
PaymentStatusCell.displayName = DataTableCell.displayName;

const PaymentAmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
PaymentAmountCell.displayName = DataTableCell.displayName;

const RelatedNameCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: COLORS.brand, cursor: 'pointer', fontWeight: '500' }}>{children}</span>
  </DataTableCell>
);
RelatedNameCell.displayName = DataTableCell.displayName;

const RelatedAmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
RelatedAmountCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const NPSPOpportunityStripe = () => {
  const [activeNav, setActiveNav] = useState('Record Home');
  const [relatedTab, setRelatedTab] = useState(0);

  // Recurring payments (monthly)
  const recurringPayments = useMemo(() => [
    { id: 'p-1', date: '2025-11-15', amount: 50, chargeId: 'ch_3QR...A1B', status: 'Succeeded' },
    { id: 'p-2', date: '2025-12-15', amount: 50, chargeId: 'ch_3QS...C2D', status: 'Succeeded' },
    { id: 'p-3', date: '2026-01-15', amount: 50, chargeId: 'ch_3QT...E3F', status: 'Succeeded' },
    { id: 'p-4', date: '2026-02-15', amount: 50, chargeId: '', status: 'Scheduled' },
  ], []);

  // Related donations
  const relatedDonations = useMemo(() => [
    { id: 'rd-1', name: 'Chen - Year-End 2024', amount: 100, date: '2024-12-20', campaign: 'Year-End Appeal 2024' },
    { id: 'rd-2', name: 'Chen - Spring Campaign', amount: 75, date: '2024-05-15', campaign: 'Spring Campaign 2024' },
  ], []);

  return (
    <ShowcasePageWrapper
      title="Chen - Monthly Giving"
      description="Donation/opportunity record page (Stripe)"
      icon="opportunity"
      iconCategory="standard"
    >
      {/* Record Header + Stage Path */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#FCB95B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon category="standard" name="opportunity" size="medium" style={{ fill: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Donation
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary, margin: 0 }}>
                  Chen - Monthly Giving
                </h1>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '13px', color: COLORS.textSecondary, alignItems: 'center' }}>
                <span style={{ fontSize: '20px', fontWeight: '700', color: COLORS.success }}>$50</span>
                <Badge label="Recurring" color={COLORS.stripe} bgColor="#f0edff" />
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon category="utility" name="recurring_exception" size="xx-small" style={{ fill: COLORS.stripe, marginRight: '4px' }} />
                  Monthly
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={COLORS.stripe}>
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  Stripe
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button label="Edit" variant="neutral" />
            <Button label="Send Receipt" variant="brand" />
          </div>
        </div>
        <StagePath stages={['Pledged', 'Active', 'Acknowledged']} currentStage="Active" />
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Three-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '25% 50% 25%', gap: '16px', alignItems: 'start' }}>

        {/* LEFT COLUMN — Highlights */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>Gift Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Monthly', value: '$50', icon: 'recurring_exception', color: COLORS.stripe },
                { label: 'Total Raised', value: '$150', icon: 'moneybag', color: COLORS.success },
                { label: 'Matching Gift', value: '$150', icon: 'connected_apps', color: COLORS.brand },
                { label: 'Total Impact', value: '$300', icon: 'trending', color: COLORS.purple },
              ].map(m => (
                <div key={m.label} style={{ backgroundColor: COLORS.bg, borderRadius: '4px', padding: '12px', textAlign: 'center' }}>
                  <Icon category="utility" name={m.icon} size="small" style={{ fill: m.color, marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.textPrimary }}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Stripe Payment Method */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Payment Method</h3>
            <div style={{ padding: '12px', backgroundColor: '#f9f9ff', borderRadius: '4px', border: `1px solid ${COLORS.stripe}22` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill={COLORS.stripe}>
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textPrimary }}>Visa •••• 4242</div>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>Expires 12/2027</div>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                Customer ID: <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>cus_RbN8x...</span>
              </div>
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              {[
                ['Next Charge', 'Feb 15, 2026'],
                ['Started', 'Nov 15, 2025'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: COLORS.textSecondary }}>{label}</span>
                  <span style={{ fontWeight: '500', color: COLORS.textPrimary }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Matching Gift */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Matching Gift</h3>
            <div style={{ padding: '12px', backgroundColor: '#eef4ff', borderRadius: '4px', border: `1px solid ${COLORS.brand}22` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: COLORS.brand }}>1:1 Match</span>
                <Badge label="Active" color={COLORS.success} bgColor="#e6f9f0" />
              </div>
              <div style={{ fontSize: '22px', fontWeight: '300', color: COLORS.textPrimary, marginBottom: '4px' }}>$50/mo</div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>TechCorp Employee Match</div>
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              {[
                ['Match Total', '$150'],
                ['Started', 'Nov 2025'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: COLORS.textSecondary }}>{label}</span>
                  <span style={{ fontWeight: '500', color: COLORS.textPrimary }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Detail + Related */}
        <div>
          {/* Gift Details */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Gift Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="Opportunity Name" value="Chen - Monthly Giving" />
              <FieldDisplay label="Amount" value="$50.00" />
              <FieldDisplay label="Primary Contact" value="David Chen" isLink />
              <FieldDisplay label="Account" value="Chen Household" isLink />
              <FieldDisplay label="Stage" value="Active" />
              <FieldDisplay label="Record Type" value="Donation" />
              <FieldDisplay label="Gift Type" value="Recurring - Monthly" />
              <FieldDisplay label="Payment Method" value="Stripe" />
              <FieldDisplay label="Close Date" value="November 15, 2025" />
              <FieldDisplay label="Date Received" value="November 15, 2025" />
              <FieldDisplay label="Primary Campaign" value="Monthly Giving Program" isLink />
              <FieldDisplay label="Fiscal Year" value="FY2026" />
              <FieldDisplay label="Acknowledgment Status" value="Auto-Receipt Enabled" />
              <FieldDisplay label="Last Receipt" value="January 15, 2026" />
              <div style={{ marginBottom: '16px' }}>
                <div style={fieldLabelStyle}>Opportunity Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.teal,
                    color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700',
                  }}>JR</div>
                  <span style={{ fontSize: '14px', color: COLORS.brand, cursor: 'pointer' }}>Jessica Rodriguez</span>
                </div>
              </div>
              <FieldDisplay label="Stripe Subscription ID" value="sub_1QRXyz..." />
            </div>
          </div>

          {/* Related Lists */}
          <div style={cardStyle}>
            <Tabs id="opp-related-tabs" selectedIndex={relatedTab} onSelect={(index) => setRelatedTab(index)}>
              <TabsPanel label={`Payments (${recurringPayments.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0edff', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                        ${recurringPayments.filter(p => p.status === 'Succeeded').length * 50} raised so far
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.stripe }}>
                        {recurringPayments.filter(p => p.status === 'Succeeded').length} payments
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      Next payment on February 15, 2026
                    </div>
                  </div>
                  <DataTable items={recurringPayments} id="payments-table" fixedLayout>
                    <DataTableColumn label="Date" property="date" width="120px" />
                    <DataTableColumn label="Amount" property="amount" width="100px">
                      <PaymentAmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Charge ID" property="chargeId" width="140px" />
                    <DataTableColumn label="Status" property="status" width="120px">
                      <PaymentStatusCell />
                    </DataTableColumn>
                  </DataTable>
                </div>
              </TabsPanel>
              <TabsPanel label={`Related Gifts (${relatedDonations.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={relatedDonations} id="related-donations-table" fixedLayout>
                    <DataTableColumn label="Name" property="name" width="200px">
                      <RelatedNameCell />
                    </DataTableColumn>
                    <DataTableColumn label="Amount" property="amount" width="100px">
                      <RelatedAmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Date" property="date" width="120px" />
                    <DataTableColumn label="Campaign" property="campaign" width="180px" />
                  </DataTable>
                </div>
              </TabsPanel>
            </Tabs>
          </div>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div>
          {/* Quick Actions */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <QuickActionButton icon="email" label="Send Receipt" />
              <QuickActionButton icon="edit" label="Update Card" />
              <QuickActionButton icon="task" label="New Task" />
              <QuickActionButton icon="note" label="Add Note" />
              <QuickActionButton icon="close" label="Cancel Sub" />
              <QuickActionButton icon="copy" label="Clone" />
            </div>
          </div>

          {/* Donor Snapshot */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Donor Snapshot</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#E27B7B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: '700', color: COLORS.white,
              }}>DC</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand, cursor: 'pointer' }}>David Chen</div>
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Sustainer &middot; Monthly Donor</div>
              </div>
            </div>
            {[
              ['Lifetime Giving', '$325'],
              ['Total Gifts', '7'],
              ['Avg Gift', '$46'],
              ['Donor Since', 'Nov 2024'],
              ['Recurring', 'Yes'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.bg}` }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{label}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Campaign Info */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Campaign Info</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '6px', backgroundColor: '#F49756',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon category="standard" name="campaign" size="x-small" style={{ fill: 'white' }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand, cursor: 'pointer' }}>Monthly Giving Program</div>
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Sustainer &middot; Active</div>
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>Progress</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>$42K / $60K</span>
              </div>
              <div style={{ height: '6px', backgroundColor: '#e5e5e5', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: '70%', height: '100%', backgroundColor: COLORS.brand, borderRadius: '3px' }} />
              </div>
            </div>
            {[
              ['Active Donors', '147'],
              ['Avg Monthly', '$286'],
              ['This Donor', '$50/mo'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{label}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default NPSPOpportunityStripe;
